import React, { useEffect, useState } from 'react';
import { ArrowLeft, Bell, Calendar, Megaphone, Check, Users, CalendarClock, CalendarPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useTokens } from '@/lib/theme';
import { Card, Empty } from '@/components/shared';

const typeMeta = {
  campaign: { icon: Megaphone, label: 'Kampanya', color: 'accent' },
  event: { icon: Calendar, label: 'Etkinlik', color: 'gold' },
  notification: { icon: Bell, label: 'Bildirim', color: 'ink' },
  reward: { icon: Bell, label: 'Ödül', color: 'pos' },
};

const icsDate = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`;
};

const googleCalUrl = (n) => {
  const start = new Date(n.eventDate);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: n.title || 'Bella Maison Etkinliği',
    dates: `${icsDate(start)}/${icsDate(end)}`,
    details: n.body || '',
  });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
};

const addToCalendar = (n) => {
  const start = new Date(n.eventDate);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const esc = (s) => String(s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Bella Maison//Mimar Partner//TR',
    'CALSCALE:GREGORIAN', 'BEGIN:VEVENT', `UID:${n.id}@bellamaison`,
    `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsDate(start)}`, `DTEND:${icsDate(end)}`,
    `SUMMARY:${esc(n.title)}`, `DESCRIPTION:${esc(n.body)}`, 'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bella-maison-etkinlik.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast.success('Etkinlik takvimine eklendi');
};

export default function Events() {
  const navigate = useNavigate();
  const tk = useTokens();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmedId, setConfirmedId] = useState(null);

  const load = () => api.get('/architect/notifications').then((r) => setNotes(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleRsvp = async (id) => {
    try {
      const r = await api.post(`/architect/events/${id}/rsvp`);
      setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, rsvped: r.data.rsvped, rsvpCount: r.data.rsvpCount } : n)));
      if (r.data.rsvped) {
        setConfirmedId(id);
        toast.success('Katılımın onaylandı', { description: 'Etkinliği takvimine eklemeyi unutma.' });
      } else {
        if (confirmedId === id) setConfirmedId(null);
        toast.success('Katılımın iptal edildi');
      }
    } catch {
      toast.error('İşlem başarısız');
    }
  };

  return (
    <div className="px-5 py-6 fade-up" data-testid="events-page">
      <button onClick={() => navigate('/app')} className="flex items-center gap-1.5 text-navy/50 text-sm mb-4" data-testid="events-back">
        <ArrowLeft strokeWidth={1.25} className="w-4 h-4" /> Geri
      </button>
      <h1 className="font-serif text-2xl text-navy mb-5">Etkinlikler & Bildirimler</h1>

      {loading ? (
        <div className="text-navy/40 text-sm">Yükleniyor...</div>
      ) : notes.length === 0 ? (
        <Empty text="Bildirim yok" />
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {notes.map((n) => {
            const meta = typeMeta[n.type] || typeMeta.notification;
            const metaColor = tk[meta.color];
            return (
              <Card key={n.id} className="overflow-hidden" data-testid={`event-card-${n.id}`}>
                {n.image && <img src={n.image} alt="" className="w-full h-36 object-cover" />}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {n.isNew && (
                      <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full text-cream bg-redx" data-testid="pill-new">
                        Yeni
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ color: metaColor, backgroundColor: metaColor + '26' }}>
                      <meta.icon strokeWidth={1.5} className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="text-xs text-navy/40">{formatDate(n.date)}</span>
                  </div>
                  <p className="font-serif text-lg text-navy leading-snug">{n.title}</p>
                  <p className="text-sm text-navy/60 mt-1 leading-relaxed">{n.body}</p>

                  {n.eventDate && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1.5 text-xs text-gold mb-2">
                        <CalendarClock strokeWidth={1.25} className="w-3.5 h-3.5" /> {formatDate(n.eventDate)}
                      </div>
                      {confirmedId === n.id && (
                        <div className="flex items-center gap-2 bg-pos/10 border border-greenx/30 rounded-md px-3 py-2 mb-2" data-testid={`rsvp-confirm-${n.id}`}>
                          <Check strokeWidth={1.5} className="w-4 h-4 text-greenx shrink-0" />
                          <p className="text-xs text-greenx">Katılımın onaylandı! Etkinliği takvimine eklemeyi unutma.</p>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => addToCalendar(n)}
                          data-testid={`add-to-calendar-${n.id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-navy/20 text-navy px-3 py-1.5 rounded-full active:scale-95 transition-transform"
                        >
                          <CalendarPlus strokeWidth={1.25} className="w-3.5 h-3.5" /> Takvime Ekle
                        </button>
                        <a
                          href={googleCalUrl(n)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`google-calendar-${n.id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-navy/20 text-navy px-3 py-1.5 rounded-full active:scale-95 transition-transform"
                        >
                          <Calendar strokeWidth={1.25} className="w-3.5 h-3.5" /> Google Takvim
                        </a>
                      </div>
                    </div>
                  )}

                  {n.rsvpEnabled && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-navy/10">
                      <span className="flex items-center gap-1.5 text-xs text-navy/50">
                        <Users strokeWidth={1.25} className="w-3.5 h-3.5" /> {n.rsvpCount || 0} katılımcı
                      </span>
                      <button
                        onClick={() => toggleRsvp(n.id)}
                        data-testid={`rsvp-btn-${n.id}`}
                        className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full transition-all active:scale-95 ${n.rsvped ? 'bg-greenx text-cream' : 'bg-navy text-cream'}`}
                      >
                        {n.rsvped ? <><Check strokeWidth={1.5} className="w-4 h-4" /> Katılıyorsun</> : 'Katılacağım'}
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
